import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createRoute } from 'routes/api';
import ModalShell from 'shared/components/ui/ModalShell';
import { buttonClasses } from 'styles/tailwindClassnames';
import { Connection } from 'shared/types/ObjectTypes';

// Reuses this very record as a new Route's provider: one account, two roles,
// one set of credentials. Destinations are chosen afterwards, on the flow map.
// The new Route gets its own name — prefilled with the Connection's for
// convenience, but decoupled: the user can (and often should) change it.
const UseAsDataSourceModal = ({
  connection,
  connectionLoaded,
  creating,
  setCreating,
  onClose,
}: {
  connection: Connection;
  connectionLoaded: boolean;
  // Held by the page, so a create still running keeps the button off when the
  // modal is closed and opened again.
  creating: boolean;
  setCreating: (creating: boolean) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const [routeName, setRouteName] = useState(connection.name);

  const handleCreate = async () => {
    if (!keycloak.token || creating) return;
    // Backstop for the connectionLoaded gate: never create a Route from
    // the empty placeholder record.
    if (!connectionLoaded) return;
    const ownerId = connection.owner?.id;
    if (!ownerId) {
      toast.error(
        t('Something went wrong. Please try again or contact support.'),
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }
    setCreating(true);
    try {
      await createRoute(keycloak.token, {
        name: routeName.trim() || connection.name,
        owner: ownerId,
        data_providers: [connection.id],
        destinations: [],
      });
      onClose();
      // createRoute returns the route RECORD, which the URL no longer wants:
      // route pages address the provider integration, which here is this
      // destination taking on the data-source role. Landing on the provider
      // resolves its default route, and that is unambiguously the record we
      // just made — the button offering this action is only rendered when the
      // connection has NO routes yet (ConnectionDetailsModal), so there is no
      // older sibling for `default_route` to prefer.
      navigate(`/routes/${connection.id}`, {
        state: { highlightAddDestination: true },
      });
    } catch (err: any) {
      console.log(err);
      if (err?.response?.status !== 401) {
        toast.error(
          t('Something went wrong. Please try again or contact support.'),
          { position: toast.POSITION.TOP_RIGHT },
        );
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <ModalShell
      title={t('Use {{name}} as a data source', { name: connection.name })}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className={buttonClasses.primaryAction}
            disabled={creating}
            onClick={handleCreate}
          >
            {t('Create Route')}
          </button>
          <button
            type="button"
            className={buttonClasses.cancelAction}
            onClick={onClose}
          >
            {t('Cancel')}
          </button>
        </>
      }
    >
      <p className="text-body-md text-ui-text-primary-text">
        {t(
          'This creates a Route with {{name}} as its source. The same account keeps its role as a destination and its credentials stay shared, and you choose where the data goes next.',
          { name: connection.name },
        )}
      </p>
      <div className="mt-4 flex flex-col">
        <label
          htmlFor="data_source_route_name"
          className="mb-1 text-body-sm font-bold text-ui-text-primary-text"
        >
          {t('Route Name')}
        </label>
        <input
          id="data_source_route_name"
          data-testid="ManageDestinationDetails-route-name-input"
          value={routeName}
          onChange={(e) => setRouteName(e.target.value)}
          className="w-full rounded border border-ui-field-outline p-1.5 px-2 transition-colors duration-300"
        />
      </div>
    </ModalShell>
  );
};

export default UseAsDataSourceModal;
